/* eslint-disable max-lines */
import _ from 'lodash';
import store from 'state/store/configureStore';
import { getTeams as getTeamsOnRange } from 'common/services/teams.service';
import {
  getHistoryInfo,
  teamsToContributorsResolver
} from '../people/views/individuals/teamHistoryUtil';
import { metricNames } from '@linearb/linjs-common';
import { calculatePercentage } from 'common/utils/utils';
import { mapCycleTimeStatisticsToPipelineMilestones } from 'components/DeliveryPipeline';
import { getDefaultFormatter } from 'common/utils/time.utils';
import { getDurationLevels } from '@linearb/linui-utils';

import { getNotifications } from 'common/services/notifications.service';
import { notificationToUiMapper } from './components/Notifications/notification.service';
import {
  LOCAL_STORAGE_KEYS,
  TRACKING,
  VIEW_PATH_KEYS
} from 'common/constants/constants';
import AppRouter from 'common/AppRouter';
import * as TrackingService from 'common/services/tracking.service';
import {
  convertMetricsNameToV3,
  getMetricsV3
} from 'common/services/metrics.service';
import {
  getTeamSettings,
  updateTeamSettings
} from 'common/services/settings.service';
import { SHOW_SLACK_NOTIFICATION_PARAM } from '../settings/views/Slack/SlackSettings';
import { createSelectTeamSlackChannelModalOptions } from 'modals/modal-options-factory';
import QueryString from 'query-string';
import { isIterationsPeriodKeySelectedSelector } from 'state/selectors/ui-state';
import { getIsGitScopeByKey } from 'components/TeamGitScopeBanner/utils';

export const CYCLE_TIME_METRICS_TITLES = {
  CODING: 'CODING',
  PICKUP: 'PICKUP',
  REVIEW: 'REVIEW',
  DEPLOY: 'DEPLOY'
};

export function compute5IterationsData(iterations, name) {
  const isIterationsPeriodKeySelected = isIterationsPeriodKeySelectedSelector(
    store.getState()
  );

  const latestIterationIdx = 4;
  const latestIteration = iterations?.[latestIterationIdx] || {};

  // if selected period - return last iteration; else - return iteration with isSelected
  const currentIteration = isIterationsPeriodKeySelected
    ? iterations.find(i => i.isSelected)
    : latestIteration;

  if (!currentIteration) {
    return [];
  }

  const currentIterationIdx = isIterationsPeriodKeySelected
    ? iterations.findIndex(i => i.isSelected)
    : latestIterationIdx;

  const currentIterationValue = currentIteration.value;
  const totalIterationsValue = _.sumBy(iterations, 'value');
  const avg =
    totalIterationsValue / iterations.filter(({ value }) => value).length;
  const pctOfAvg = calculatePercentage(currentIterationValue, avg);
  const delta =
    _.get(currentIteration, `value`, 0) -
    _.get(iterations, `[${currentIterationIdx - 1}].value`, 0);

  return {
    value: _.get(iterations, `[${currentIterationIdx}].value`, 0),
    iterations,
    delta: delta || '-',
    pctOfAvg,
    avg,
    name,
    description: name
  };
}

export const getContributorsMetricsWithHistory = async (
  contributorsMetrics,
  dates,
  teamId
) => {
  const teamsOnRange = await getTeamsOnRange(dates).catch(() => []);
  const selectedTimeContributorsMap = teamsToContributorsResolver(
    teamsOnRange,
    dates.from,
    dates.to
  );
  return contributorsMetrics.map(metricItem => {
    const selectedTimeContributorTeams = _.get(
      selectedTimeContributorsMap,
      metricItem.contributor_id,
      []
    );
    const contributorCurrentTeam = selectedTimeContributorTeams.filter(
      contributor => teamId === contributor.team_id
    );
    return {
      ...metricItem,
      team: getHistoryInfo(contributorCurrentTeam)
    };
  });
};

const CYCLE_TIME_BASE_CONFIG = [
  {
    leftTag: 'First commit',
    rightTag: 'PR issued',
    title: CYCLE_TIME_METRICS_TITLES.CODING,
    metricName: metricNames.TIME_TO_PR
  },
  {
    rightTag: 'PR review started',
    title: CYCLE_TIME_METRICS_TITLES.PICKUP,
    metricName: metricNames.TIME_TO_REVIEW
  },
  {
    rightTag: 'PR merged',
    title: CYCLE_TIME_METRICS_TITLES.REVIEW,
    metricName: metricNames.REVIEW_TIME
  },
  {
    rightTag: 'Release',
    title: CYCLE_TIME_METRICS_TITLES.DEPLOY,
    metricName: metricNames.TIME_TO_PROD
  }
];

export const getCycleTimeConfigurations = (
  pipelineMilestones,
  disableTags,
  metricsCalculationConfig = {},
  metricsIncludedWeekends = {}
) => {
  return CYCLE_TIME_BASE_CONFIG.map((cycleTimeConfig, idx) => {
    const milestone = pipelineMilestones[idx] || {
      value: 'N/A',
      subTitle: '',
      color: 'gray'
    };
    const isIncludeWeekends = Object.values(metricsIncludedWeekends)[idx];
    return {
      ...cycleTimeConfig,
      value: milestone.value === '' ? 'N/A' : milestone.value,
      description: milestone.subTitle,
      color: milestone.color,
      tooltip: milestone.tooltip,
      ...(disableTags ? { leftTag: null, rightTag: null } : {}),
      isIncludeWeekends
    };
  });
};

export const createTrendDataV3 = (
  metrics,
  current5Iterations,
  metricsCalculationConfig,
  benchmarkOrgSize
) => {
  const xData = current5Iterations.reduce(
    (acc, { id, name, range }, index) => ({
      ...acc,
      [name]: { id, name, dates: range, index }
    }),
    {}
  );
  const convertV3Format = v3Metrics => {
    return Object.keys(v3Metrics).reduce((acc, curr) => {
      acc[curr.split(':')[0]] = v3Metrics[curr];
      return acc;
    }, {});
  };
  const metricsData = _.get(metrics, '[0].data', []);
  const yWorkBreakDownData = metricsData.map(item => {
    const {
      [metricNames.NEW_WORK]: newWork,
      [metricNames.REWORK]: rework,
      [metricNames.REFACTOR]: refactor,
      [metricNames.COMMIT_CODE_CHANGES]: changes
    } = item || {};
    return { newWork, rework, refactor, changes };
  });
  const yCycleTimeData = metricsData.map(item => {
    const metrics = convertV3Format(item || {});
    const {
      [metricNames.TIME_TO_PR]: timeToPr,
      [metricNames.TIME_TO_REVIEW]: timeToReview,
      [metricNames.REVIEW_TIME]: timeToMerged,
      [metricNames.TIME_TO_PROD]: timeToProd
    } = metrics;
    const pipelineMilestones = mapCycleTimeStatisticsToPipelineMilestones(
      metrics,
      {},
      metricsCalculationConfig,
      benchmarkOrgSize
    );
    const totalCycleTimeInMinutes =
      _.sum(
        pipelineMilestones
          .map(({ rawValue }) => rawValue)
          .filter(value => !!value)
      ) || 0;

    const cycleTimeAverage = getDurationLevels({
      minutes: totalCycleTimeInMinutes,
      ignoreZeros: true,
      format: getDefaultFormatter({
        minutes: totalCycleTimeInMinutes,
        daysUnit: ' days',
        hoursUnit: ' hours',
        minutesUnit: ' minutes',
        yearsUnit: ' years'
      }),
      joiner: ' '
    });

    return {
      cycleTimeConfigurations: getCycleTimeConfigurations(
        pipelineMilestones,
        true
      ),
      avgTime: cycleTimeAverage,
      totalCycleTime: totalCycleTimeInMinutes,
      timeToProd,
      timeToPr,
      timeToReview,
      timeToMerged
    };
  });
  const yReviewDepthData = metricsData.map(item => {
    const metrics = item || {};
    return [metrics[metricNames.REVIEWS], metrics[metricNames.REVIEW_DEPTH]];
  });
  const yReleasesData = metricsData.map(item => {
    const metrics = item || {};
    return [metrics[metricNames.RELEASES_COUNT]];
  });
  return {
    xData,
    yWorkBreakDownData,
    yCycleTimeData,
    yReviewDepthData,
    yReleasesData
  };
};

/* istanbul ignore next */
export const loadTeamNotifications = async ({
  teamId,
  withLoading,
  setIsNotificationsLoading,
  setNotifications,
  history,
  localStorageAccessor
}) => {
  if (withLoading) {
    setIsNotificationsLoading(true);
  }
  const response = await getNotifications(teamId);

  const _lastViewTime = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.LAST_VIEW_NOTIFICATIONS(teamId)
  );
  const lastViewTime = _lastViewTime ? new Date(_lastViewTime) : null;
  const notifications = response.items.map(
    notificationToUiMapper(lastViewTime, history)
  );
  setNotifications(notifications);
  setIsNotificationsLoading(false);
};

/* istanbul ignore next */
export const goToBranchesOrderBy = (
  orderBy,
  uiState,
  selectedTeam,
  history,
  metricName = null,
  dateFilterKey = 'updated'
) => {
  const searchParams = {
    order_by: orderBy,
    [dateFilterKey]: `${uiState.dates.from},${uiState.dates.to}`,
    teams: selectedTeam && selectedTeam.id,
    ...(metricName ? { metric_name: metricName } : {})
  };
  history.push({
    pathname: AppRouter.BRANCH.path,
    search: `?${QueryString.stringify(searchParams)}`
  });
};

/* istanbul ignore next */
export const fetchContributorsMetrics = async ({
  teamId,
  contributorsOrder,
  dates,
  metricsCalculationStrategy,
  gitScopeToggle
}) => {
  try {
    const requestedMetrics = [
      metricNames.ACTIVE_BRANCHES,
      metricNames.DONE_BRANCHES,
      metricNames.REVIEWS,
      metricNames.ACTIVE_DAYS,
      metricNames.REVIEW_DEPTH
    ];
    const timeFrame = {
      after: dates.from,
      before: dates.to
    };
    const options = {
      return_no_data: true,

      order_by: {
        [contributorsOrder.by]: contributorsOrder.dir
      },
      team_ids: [teamId],
      group_by: 'contributor',
      roll_up: 'custom',
      ...(gitScopeToggle !== null
        ? {
            team_git_scope_disabled: !getIsGitScopeByKey(
              gitScopeToggle,
              VIEW_PATH_KEYS.DASHBOARD
            )
          }
        : {})
    };

    const contributorsV3Metrics = await getMetricsV3({
      ...options,
      requested_metrics: convertMetricsNameToV3(
        requestedMetrics,
        metricsCalculationStrategy
      ),
      time_ranges: [timeFrame]
    });
    const mappedMetrics = contributorsV3Metrics
      .map(({ group, data }) =>
        data.map(teamData => ({ ...teamData, contributor_id: group }))
      )
      .flat();

    return mappedMetrics;
  } catch (e) {
    return [];
  }
};

/* istanbul ignore next */
export const toggleTeamSlackModal = ({
  firstUser = false,
  account,
  toggleSnackBar,
  patchAccount,
  setTeamSlackSettings,
  toggleModal,
  selectedTeamId,
  selectedTeam
}) => {
  const onSubmit = async ({ name, id }) => {
    await updateTeamSettings(selectedTeamId, {
      teamSlackChannel: { name, id }
    });
    const { settings = {}, id: accountId } = account;
    const showSlackNotification = _.get(
      settings,
      SHOW_SLACK_NOTIFICATION_PARAM,
      null
    );
    if (showSlackNotification !== false) {
      patchAccount(accountId, {
        settings: { ...settings, [SHOW_SLACK_NOTIFICATION_PARAM]: false }
      });
      toggleSnackBar({ isOpen: false });
    }
    getTeamSettings(selectedTeamId).then(settings =>
      setTeamSlackSettings({
        channel: _.get(settings, 'notifications.teamSlackChannel.value', null),
        isEnabled: _.get(
          settings,
          'notifications.teamIsNotificationsEnabled.value',
          false
        )
      })
    );
    toggleModal();
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.SLACK,
      click_type: TRACKING.CLICK.CONNECT_TEAM,
      team_name: selectedTeam.name
    });
  };

  toggleModal(
    createSelectTeamSlackChannelModalOptions({
      onSubmit,
      onCancel: () => toggleModal(),
      title: <span>You successfully connected Slack!</span>,
      firstUser
    })
  );
};
